import type { ShadowRunSummary, RunSummaryItem, EventDataset, MatchStatus, ShadowRunStatus } from '@/lib/backtest/types';

export function stripTrailingZero(value: string) {
  return value.replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
}

export function parseDecimalString(value: string | null | undefined) {
  if (value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function formatPnl(v: number) {
  const prefix = v >= 0 ? '+' : '-';
  return `${prefix}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatPct(v: number | null | undefined, fractionDigits = 2, signed = true) {
  if (v == null) return '—';
  const n = Number(v);
  const prefix = signed && n >= 0 ? '+' : '';
  return `${prefix}${stripTrailingZero(n.toFixed(fractionDigits))}%`;
}

export function formatMoneyString(value: string | null | undefined) {
  const parsed = parseDecimalString(value);
  if (parsed == null) return '—';
  return formatPnl(parsed);
}

export function formatDecimalPct(value: string | null | undefined, fractionDigits = 2) {
  const parsed = parseDecimalString(value);
  if (parsed == null) return '—';
  return formatPct(parsed, fractionDigits);
}

export function formatRunCreatedAt(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const datePart = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  return `${datePart} ${timePart}`;
}

export function formatDatasetDate(value?: string) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatDatasetRange(dataset: EventDataset) {
  const start = formatDatasetDate(dataset.start_time_utc);
  const end = formatDatasetDate(dataset.end_time_utc);
  if (!start && !end) return null;
  if (start && end) return `${start} - ${end}`;
  return start || end;
}

export function formatDatasetLine(dataset: EventDataset | undefined, slug: string) {
  const title = dataset?.event_title ?? slug.replace(/-/g, ' ');
  const range = dataset ? formatDatasetRange(dataset) : null;
  return range ? `${title} ${range}` : title;
}

export function getOptionalMetric(
  summary: RunSummaryItem['summary'],
  keys: string[],
) {
  if (!summary) return null;

  for (const key of keys) {
    const value = (summary as Record<string, unknown>)[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
  }

  return null;
}

export function formatTimeAgo(ts: string): string {
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (diff < 60) return `${diff} sec ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  const days = Math.floor(diff / 86400);
  return days === 1 ? '1 day ago' : `${days} days ago`;
}

export function formatHeartbeat(ts: string): string {
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (diff < 60) return `${diff} sec ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  return `${Math.floor(diff / 3600)} hr ago`;
}

export function formatRunStatusLabel(status?: string | null, currentStep?: string | null) {
  const source = status || currentStep || 'RUNNING';
  return source.charAt(0) + source.slice(1).toLowerCase().replace(/_/g, ' ');
}

const NEUTRAL = { bg: 'bg-[#757575]', text: 'text-white/60', dot: 'bg-white/30' };

export function getShadowStatusConfig(status: MatchStatus): { bg: string; text: string; label: string; dot: string } {
  switch (status) {
    case 'active':
      return { bg: 'bg-[#0a3b2f]', text: 'text-[#26d39a]', label: 'Live', dot: 'bg-[#26d39a]' };
    case 'paused':
      return { ...NEUTRAL, label: 'Paused' };
    case 'completed':
      return { ...NEUTRAL, label: 'Completed' };
    case 'stopped':
      return { ...NEUTRAL, label: 'Stopped' };
    case 'scheduled':
      return { ...NEUTRAL, label: 'Scheduled' };
    case 'skipped':
      return { ...NEUTRAL, label: 'Skipped' };
    case 'failed':
      return { bg: 'bg-[#534535]', text: 'text-[#F5BA80]', label: 'Failed', dot: 'bg-[#F5BA80]' };
    case 'unknown':
    default:
      return { ...NEUTRAL, label: 'Unknown' };
  }
}

export type ExecutionTier = 'live' | 'shadow' | 'backtest';

const TIER_LABEL: Record<ExecutionTier, string> = {
  live: 'Live',
  shadow: 'Paper',
  backtest: 'Backtest',
};

/**
 * 卡片执行层徽标：tierLabel = 层名（Live/Paper/Backtest），颜色/圆点 = run 状态。
 * statusLabel = 终态/非激活时的状态后缀（active 时为 null，自明无需标注；backtest 恒 null）。
 */
export function getTierBadge(
  tier: ExecutionTier,
  status: string,
): { tierLabel: string; statusLabel: string | null; bg: string; text: string; dot: string; pulse: boolean } {
  const tierLabel = TIER_LABEL[tier];
  if (tier === 'backtest') {
    if (status === 'FAILED') {
      return { tierLabel, statusLabel: null, bg: 'bg-[#534535]', text: 'text-[#F5BA80]', dot: 'bg-[#F5BA80]', pulse: false };
    }
    return { tierLabel, statusLabel: null, ...NEUTRAL, pulse: false };
  }
  const cfg = getShadowStatusConfig(status as MatchStatus);
  return {
    tierLabel,
    statusLabel: status === 'active' ? null : cfg.label,
    bg: cfg.bg,
    text: cfg.text,
    dot: cfg.dot,
    pulse: status === 'active',
  };
}

export function getShadowCardMode(status: ShadowRunStatus): 'live' | 'scheduled' | 'terminal' {
  if (status === 'active') return 'live';
  if (status === 'scheduled') return 'scheduled';
  return 'terminal';
}

/** Split session shadow runs by trading mode. Missing mode → paper (defensive). */
export function partitionRunsByTradingMode(runs: ShadowRunSummary[]): {
  paper: ShadowRunSummary[];
  live: ShadowRunSummary[];
} {
  const paper: ShadowRunSummary[] = [];
  const live: ShadowRunSummary[] = [];
  for (const run of runs) {
    (run.mode === 'live' ? live : paper).push(run);
  }
  return { paper, live };
}
